import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function ControlIcon({ name }) {
  const paths = {
    filters: <><path d="M4 7h16M4 17h16" /><circle cx="9" cy="7" r="2" /><circle cx="15" cy="17" r="2" /></>,
    categories: <><rect x="3" y="3" width="7" height="7" rx="1" /><circle cx="17" cy="7" r="4" /><path d="m7 14 4 7H3Z" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
    portals: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 7h10M7 11h4M7 15h4M15 11h2M15 15h2" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    save: <><path d="M5 3h12l4 4v14H3V3Z" /><path d="M7 3v6h10V3M7 21v-8h10v8" /></>,
    reset: <><path d="M3 5v6h6M3 11a9 9 0 1 1 2 7" /></>,
    edit: <><path d="m15 4 5 5M4 20l5-1L21 7l-5-5L4 14Z" /></>,
    trash: <><path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    minus: <path d="M5 12h14" />,
    list: <><path d="M9 5h12M9 12h12M9 19h12" /><path d="M3 5h1M3 12h1M3 19h1" /></>,
    'grid-small': <>{[3, 10, 17].flatMap(x => [3, 10, 17].map(y => <rect key={`${x}-${y}`} x={x} y={y} width="4" height="4" rx=".5" />))}</>,
    'grid-large': <>{[3, 14].flatMap(x => [3, 14].map(y => <rect key={`${x}-${y}`} x={x} y={y} width="7" height="7" rx="1" />))}</>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    keywords: <><path d="M4 5h10M4 10h7M4 15h7M4 20h10" /><path d="m15 12 3 3 3-3m-3 3V5" /></>,
    search: <><circle cx="10.5" cy="10.5" r="7" /><path d="m16 16 5 5" /></>,
    bullet: <><path d="M3 8h10c4 0 8 4 8 4s-4 4-8 4H3Z" /><path d="M7 8v8M14 8.2v7.6" /></>,
  };
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

export function InfoPopover({ title, children }) {
  const [position, setPosition] = useState(null);
  const button = useRef(null);
  const panel = useRef(null);
  const id = useId();
  useEffect(() => {
    if (!position) return;
    const dismiss = event => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'pointerdown' && (button.current?.contains(event.target) || panel.current?.contains(event.target))) return;
      if (event.type === 'scroll' && panel.current?.contains(event.target)) return;
      setPosition(null);
      if (event.type === 'keydown') button.current?.focus();
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', dismiss);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', dismiss);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [position]);
  return <>
    <button ref={button} type="button" className="control-info" aria-label={`Objašnjenje: ${title}`} aria-expanded={Boolean(position)} aria-controls={position ? id : undefined}
      onClick={event => {
        event.stopPropagation();
        if (position) return setPosition(null);
        const rect = button.current.getBoundingClientRect();
        const width = Math.min(280, window.innerWidth - 24);
        setPosition({ left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)),
          ...(rect.bottom + 240 > window.innerHeight ? { bottom: window.innerHeight - rect.top + 8 } : { top: rect.bottom + 8 }), width });
      }} title={title}>i</button>
    {position && createPortal(<div ref={panel} id={id} className="control-help-popover" role="dialog" aria-label={title} style={position} onClick={event => event.stopPropagation()}>
      <div className="control-help-heading"><strong>{title}</strong><button type="button" aria-label="Zatvori objašnjenje" onClick={() => { setPosition(null); button.current?.focus(); }}>×</button></div>
      {children}
    </div>, document.body)}
  </>;
}

export function CompactSearchOptions({ multiKeywordEnabled, multiKeyword, setMultiKeyword, keywordMode, setKeywordMode, searchAllEnabled = false, searchAll, setSearchAll }) {
  return <div className="compact-search-options">
    {multiKeywordEnabled && <div className="compact-option-group">
      <label className={`compact-option ${multiKeyword ? 'is-on' : ''}`} title="Više ključnih riječi">
        <input type="checkbox" aria-label="Više ključnih riječi" checked={multiKeyword} onChange={event => setMultiKeyword(event.target.checked)} />
        <span className="search-option-label">Više riječi</span>
      </label>
      {multiKeyword && <button type="button" className="keyword-mode-toggle" role="switch" aria-checked={keywordMode === 'all'} aria-label="Sve ključne riječi" title={keywordMode === 'all' ? 'Sve ključne riječi — klik za bilo koju' : 'Bilo koja ključna riječ — klik za sve'} onClick={() => setKeywordMode(keywordMode === 'all' ? 'any' : 'all')}>
        <span>{keywordMode === 'all' ? 'Sve' : 'Bilo koja'}</span>
      </button>}
    </div>}
    {searchAllEnabled && <div className="compact-option-group">
      <label className={`compact-option ${searchAll ? 'is-on' : ''}`} title="Pretraga bez filtera">
        <input type="checkbox" aria-label="Traži naslove kroz sve kategorije i portale" checked={searchAll} onChange={event => setSearchAll(event.target.checked)} />
        <span className="search-option-label">Bez filtera</span>
      </label>
    </div>}
  </div>;
}

export function SearchOptionsHelp({ multiKeywordEnabled, searchAllEnabled }) {
  return <div className="search-options-help">
    {multiKeywordEnabled && <p><span><strong>Više riječi:</strong> Enter dodaje cijelu frazu kao oznaku; „Bilo koja” / „Sve” bira podudaranje. Lupa pokreće pretragu.</span></p>}
    {searchAllEnabled && <p><span><strong>Bez filtera:</strong> pretraga naslova kroz sve kategorije, portale, države i vrijeme. Bez teksta pretrage filteri i dalje važe.</span></p>}
  </div>;
}
